import React from "react";
import { Theme } from "../../types";

interface ThemeButtonProps {
  theme: Theme;
  isSelected: boolean;
  onSelect: (theme: Theme) => void;
}

const getThemeInfo = (theme: Theme) => {
  switch (theme) {
    case "auto":
      return {
        icon: "🔄",
        name: "Auto",
        textColor: "#ffffff", // White text since it's over dark background
        preview: (
          <div style={{ display: "flex", height: "100%", width: "100%" }}>
            <div
              style={{
                flex: 1,
                background: "linear-gradient(135deg, #1a1a1a 50%, #f5f5f5 50%)",
              }}
            />
          </div>
        ),
      };
    case "dark":
      return {
        icon: "🌙",
        name: "Dark",
        textColor: "#ffffff",
        preview: (
          <div style={{ background: "#1a1a1a", height: "100%", width: "100%" }}>
            <div
              style={{
                height: "28px",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 100%)",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}
            />
          </div>
        ),
      };
    case "light":
      return {
        icon: "☀️",
        name: "Light",
        textColor: "#1a1a1a",
        preview: (
          <div style={{ background: "#f5f5f5", height: "100%", width: "100%" }}>
            <div
              style={{
                height: "28px",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.03) 0%, transparent 100%)",
                borderBottom: "1px solid rgba(0,0,0,0.1)",
              }}
            />
          </div>
        ),
      };
    case "high-contrast-dark":
      return {
        icon: "🔆",
        name: "HC Dark",
        textColor: "#ffffff",
        preview: (
          <div style={{ background: "#000", height: "100%", width: "100%" }}>
            <div
              style={{
                height: "28px",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 100%)",
                borderBottom: "2px solid #fff",
              }}
            />
          </div>
        ),
      };
    case "high-contrast-light":
      return {
        icon: "🔆",
        name: "HC Light",
        textColor: "#000000",
        preview: (
          <div style={{ background: "#fff", height: "100%", width: "100%" }}>
            <div
              style={{
                height: "28px",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, transparent 100%)",
                borderBottom: "2px solid #000",
              }}
            />
          </div>
        ),
      };
    default:
      return {
        icon: "🎨",
        name: theme,
        textColor: "var(--text-primary)",
        preview: null,
      };
  }
};

export const ThemeButton: React.FC<ThemeButtonProps> = ({
  theme,
  isSelected,
  onSelect,
}) => {
  const info = getThemeInfo(theme);

  return (
    <button
      onClick={() => onSelect(theme)}
      title={info.name}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "0",
        width: "100%",
        height: "100%",
        background: isSelected ? "rgba(62, 106, 225, 0.3)" : "var(--bg-subtle)",
        border: isSelected
          ? "2px solid var(--accent)"
          : "1px solid var(--border)",
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Preview area with name and icon inside */}
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {info.preview}

        {/* Name at top inside preview */}
        <div
          style={{
            position: "absolute",
            top: "8px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "10px",
            color: info.textColor,
            fontWeight: 700,
            whiteSpace: "nowrap",
            textShadow: theme === "auto" ? "0 1px 2px rgba(0,0,0,0.5)" : "none",
            zIndex: 1,
          }}
        >
          {info.name}
        </div>

        {/* Icon in center of preview */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "24px",
            lineHeight: 1,
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
          }}
        >
          {info.icon}
        </div>
      </div>
    </button>
  );
};
